import { useState } from "react";
import ExcelJS from "exceljs";
import { triggerBlobDownload } from "utils/Common/fileDownload";
import {
  DrawDetail,
  DrawG703Row,
  MoneyField,
} from "Pages/Admin/ProjectDetail/sections/DrawHistory/DrawHistory.types";

const CURRENCY_FMT = '"$"#,##0.00';
const MONEY_COLUMNS = [3, 4, 5, 6, 7, 9];

const amountOf = (m?: MoneyField | null) => Number(m?.amount ?? 0);

export const useG703Excel = () => {
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const exportG703Excel = async (
    drawDetail: DrawDetail | null,
    projectName?: string,
  ) => {
    if (!drawDetail) return;
    setIsExportingExcel(true);
    try {
      const rows = drawDetail.lineItems?.rows ?? [];
      const drawLabel = String(drawDetail.draw.drawNumber).padStart(2, "0");

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "CSC New England";
      workbook.created = new Date();

      // Mirrors G703Tab.tsx's column set exactly, so the file matches what's on screen.
      const sheet = workbook.addWorksheet(`Draw ${drawLabel} G703`, {
        views: [{ state: "frozen", ySplit: 3 }],
      });
      sheet.columns = [
        { width: 8 },
        { width: 34 },
        { width: 16 },
        { width: 18 },
        { width: 14 },
        { width: 16 },
        { width: 20 },
        { width: 8 },
        { width: 16 },
      ];

      sheet.mergeCells("A1:I1");
      const titleCell = sheet.getCell("A1");
      titleCell.value = `AIA G703 — Continuation Sheet — Draw #${drawLabel}${projectName ? ` — ${projectName}` : ""}`;
      titleCell.font = { bold: true, size: 13, color: { argb: "FF14508C" } };
      sheet.getRow(1).height = 22;

      const headerRow = sheet.addRow([
        "Item",
        "Description",
        "Scheduled Value",
        "Previous Application",
        "This Period",
        "Materials Stored",
        "Total Completed & Stored",
        "%",
        "Balance to Finish",
      ]);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF14508C" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      rows.forEach((row: DrawG703Row) => {
        const excelRow = sheet.addRow([
          row.aiaItemCode ?? row.aiaItem?.itemCode ?? "—",
          row.description ?? row.aiaItem?.itemName ?? "—",
          amountOf(row.sovValue),
          amountOf(row.previousBilled),
          amountOf(row.currentApplication),
          amountOf(row.currentStoredMaterial),
          amountOf(row.totalCompletedAndStored),
          row.percentageComplete ?? 0,
          amountOf(row.balanceToFinish),
        ]);
        MONEY_COLUMNS.forEach((col) => {
          excelRow.getCell(col).numFmt = CURRENCY_FMT;
        });
        excelRow.getCell(8).numFmt = '0"%"';
        excelRow.getCell(1).alignment = { horizontal: "center" };
      });

      const sum = (key: keyof DrawG703Row) =>
        rows.reduce(
          (acc, row) => acc + amountOf(row[key] as MoneyField | undefined),
          0,
        );

      const footerRow = sheet.addRow([
        "",
        "Grand Total",
        sum("sovValue"),
        sum("previousBilled"),
        sum("currentApplication"),
        sum("currentStoredMaterial"),
        sum("totalCompletedAndStored"),
        "",
        sum("balanceToFinish"),
      ]);
      footerRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEDF1F5" },
        };
        if (MONEY_COLUMNS.includes(colNumber)) cell.numFmt = CURRENCY_FMT;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      triggerBlobDownload(
        blob,
        `G703-Draw-${drawLabel}-${projectName ?? "export"}-${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } finally {
      setIsExportingExcel(false);
    }
  };

  return { exportG703Excel, isExportingExcel };
};
